import { Capacitor, registerPlugin } from "@capacitor/core";

interface NativeFileExportPlugin {
  saveText(options: {
    fileName: string;
    mimeType: string;
    content: string;
  }): Promise<{ fileName: string }>;
}

export interface ExportTextFileOptions {
  fileName: string;
  content: string;
  mimeType?: string;
}

export interface ExportTextFileResult {
  fileName: string;
  method: "native-picker" | "browser-download";
}

const nativeFileExport = registerPlugin<NativeFileExportPlugin>("GGFileExport");

export function normalizeExportFileName(value: string) {
  const normalized = value
    .normalize("NFC")
    .replace(/[\u0000-\u001f\u007f/\\:*?"<>|]/g, "-")
    .replace(/[. ]+$/g, "")
    .trim();
  return normalized || "export.json";
}

function supportsNativeFileExport() {
  return Capacitor.getPlatform() === "android"
    && Capacitor.isNativePlatform()
    && Capacitor.isPluginAvailable("GGFileExport");
}

function startBrowserDownload(fileName: string, content: string, mimeType: string) {
  if (typeof document === "undefined" || typeof URL?.createObjectURL !== "function") {
    throw new Error("当前环境不支持文件导出");
  }
  const url = URL.createObjectURL(new Blob([content], { type: mimeType }));
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * Android APK uses the system document picker and resolves only after the file
 * is written. Browser H5 keeps the normal download behavior.
 */
export async function exportTextFile({
  fileName,
  content,
  mimeType = "application/json",
}: ExportTextFileOptions): Promise<ExportTextFileResult> {
  const safeFileName = normalizeExportFileName(fileName);
  if (supportsNativeFileExport()) {
    await nativeFileExport.saveText({ fileName: safeFileName, mimeType, content });
    return { fileName: safeFileName, method: "native-picker" };
  }
  startBrowserDownload(safeFileName, content, mimeType);
  return { fileName: safeFileName, method: "browser-download" };
}
